import env from "@beam-australia/react-env"
import { isEqual } from "lodash"
import { FC, useContext, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { icons } from "src/assets"
import { PrivateLoader } from "src/components/templates"
import { Button, Checkbox, FormControlLabel, Grid, SelectInput, TextFieldLabel, Typography } from "src/components/UI"
import { SelectInputOption } from "src/components/UI/CustomUI/atoms/SelectInput/types"
import { contractorsActions } from "src/ducks/actions"
import { getContractorSelected } from "src/ducks/contractors/selectors"
import { getOnlyTerritoriesForDropdown, getTerritories } from "src/ducks/territories/selectors"
import { ContractorItem } from "src/ducks/types"
import { getDifferenceBetweenObjects, isEmpty, validateEmail } from "src/helpers"
import { UpdateRequest } from "~api/types"
import { ContractorsContext } from "../../context"
import { dispatchTypes, MODAL_TYPE } from "../../context/types"

const FILES_URL = env('FILES_URL') ?? ''

const EditContractor: FC = () => {

    const dispatch = useDispatch()
    const { dispatch: dispatchContext } = useContext(ContractorsContext)
    const contractor = useSelector(getContractorSelected)
    const territories = useSelector(getOnlyTerritoriesForDropdown)
    const allTerritories = useSelector(getTerritories)

    const [loading, setLoading] = useState(false)
    const [showWarnings, setShowWarnings] = useState(false)
    const [hasUpdatedData, setHasUpdatedData] = useState(false)

    // FORM
    const [editContractorForm, setEditContractorForm] = useState<Partial<ContractorItem>>(contractor || {})
    const [editContractorTerritory, setEditContractorTerritory] = useState<{ key: string, label: string } | null>(null)

    const contractorTerritory = allTerritories.find(territory => territory.contractors.some(c => c.id === editContractorForm?.id))

    // FUTURE: multiple territories
    // const [editContractorTerritories, setEditContractorTerritories] = useState<{ key: string, label: string }[]>([])
    // const contractorTerritories = allTerritories.filter(territory => territory.contractors.some(c => c.id === editContractorForm?.id))

    const isValidUrl = (url: string | undefined) => {
        if (!url) return false
        const urlPattern = new RegExp('^(https?:\\/\\/)?' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
        return !!urlPattern.test(url);
    }

    useEffect(() => {
        const formHasChanges = !isEqual(editContractorForm, contractor || {})
        const territoryHasChanges = editContractorTerritory?.key !== (contractorTerritory?.id ?? '')
        setHasUpdatedData(formHasChanges || territoryHasChanges)
    }, [editContractorForm, contractor, editContractorTerritory?.key, contractorTerritory?.id])

    useEffect(() => {
        setEditContractorTerritory(contractorTerritory ? { key: contractorTerritory.id, label: contractorTerritory.title } : null)
    }, [contractorTerritory?.id])

    // FUTURE: multiple territories
    // const currentTerritoryIds = contractorTerritories.map(t => t.id).sort().join(',')
    // const selectedTerritoryIds = editContractorTerritories.map(t => t.key).sort().join(',')
    // useEffect(() => { setEditContractorTerritories(contractorTerritories.map(t => ({ key: t.id, label: t.title }))) }, [contractor?.id, currentTerritoryIds])

    const validate = !isEmpty(editContractorForm?.name) &&
        !isEmpty(editContractorForm?.firstName) &&
        !isEmpty(editContractorForm?.lastName) &&
        (isEmpty(editContractorForm?.email) || validateEmail(editContractorForm?.email ? editContractorForm?.email : '')) &&
        !isEmpty(editContractorForm?.websiteUrl) &&
        isValidUrl(editContractorForm?.websiteUrl)


    const handleCancel = () => {
        dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
    }

    const handleSave = () => {
        if (validate && contractor?.id) {
            setLoading(true)
            const websiteUrlWithProtocol = editContractorForm?.websiteUrl?.startsWith('http') ? editContractorForm?.websiteUrl : `https://${editContractorForm?.websiteUrl}`
            const docuSignLinkWithProtocol = editContractorForm?.docuSignLink ? editContractorForm?.docuSignLink?.startsWith('http') ? editContractorForm?.docuSignLink : `https://${editContractorForm?.docuSignLink}` : ''
            const updateRequest: UpdateRequest = getDifferenceBetweenObjects(
                {
                    ...editContractorForm,
                    websiteUrl: websiteUrlWithProtocol,
                    docuSignLink: docuSignLinkWithProtocol,
                },
                {
                    ...contractor,
                },
                'replace'
            )
            // Single territory (one territory per contractor)
            if (contractorTerritory?.id) {
                if (editContractorTerritory?.key !== contractorTerritory?.id) {
                    if (editContractorTerritory?.key) {
                        dispatch(contractorsActions.deleteContractorTerritory({ contractorId: contractor.id as string, territoryId: contractorTerritory.id }, (succ) => {
                            if (succ) {
                                dispatch(contractorsActions.setContractorTerritory({ contractorId: contractor.id as string, territoryId: editContractorTerritory?.key as string }, (succ) => {
                                    if (succ) {
                                        toast.success.call(null, 'Contractor Territory Changed')
                                        dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
                                        setLoading(false)
                                    } else {
                                        toast.error.call(null, 'Failed to change contractor territory')
                                        setLoading(false)
                                    }
                                }))
                            } else {
                                setLoading(false)
                            }
                        }))
                    } else {
                        dispatch(contractorsActions.deleteContractorTerritory({ contractorId: contractor.id as string, territoryId: contractorTerritory.id }, (succ) => {
                            if (succ) {
                                toast.success.call(null, 'Contractor Territory Deleted')
                                dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
                                setLoading(false)
                            } else {
                                toast.error.call(null, 'Failed to delete contractor territory')
                                setLoading(false)
                            }
                        }))
                    }
                }
            } else {
                if (editContractorTerritory?.key) {
                    dispatch(contractorsActions.setContractorTerritory({ contractorId: contractor.id as string, territoryId: editContractorTerritory?.key as string }, (succ) => {
                        if (succ) {
                            toast.success.call(null, 'Contractor Territory Set')
                            dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
                            setLoading(false)
                        } else {
                            toast.error.call(null, 'Failed to set contractor territory')
                            setLoading(false)
                        }
                    }))
                }
            }
            // FUTURE: multiple territories (uncomment and replace the previous block)
            // const currentIds = contractorTerritories.map(t => t.id)
            // const selectedIds = editContractorTerritories.map(t => t.key).filter(Boolean)
            // const territoryIdsToRemove = currentIds.filter(id => !selectedIds.includes(id))
            // const territoryIdsToAdd = selectedIds.filter(id => !currentIds.includes(id))
            // const hasTerritoryChanges = territoryIdsToRemove.length > 0 || territoryIdsToAdd.length > 0
            // const runDeletesThenAdds = (...) => { ... }
            // if (hasTerritoryChanges) { runDeletesThenAdds(...) } else if (editContractorForm.active !== contractor.active) {

            if (editContractorForm.active !== contractor.active) {
                if (editContractorForm.active) {
                    dispatch(contractorsActions.activateContractor(contractor.id as string, (succ) => {
                        if (succ) {
                            const hasSomeChanges = updateRequest.length > 0
                            if (hasSomeChanges) {
                                const updateRequestFiltered = updateRequest.filter((item) => item.path !== '/active')
                                dispatch(contractorsActions.patchContractor({
                                    contractor: {
                                        ...editContractorForm,
                                    },
                                    updateRequest: updateRequestFiltered
                                }, (succ) => {
                                    if (succ) {
                                        dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
                                        toast.success.call('success', 'Contractor Activated')
                                        setLoading(false)
                                    } else {
                                        toast.error.call('error', 'Failed to activate contractor')
                                        setLoading(false)
                                    }
                                }))
                            }
                        } else {
                            setLoading(false)
                        }
                    }))
                } else {
                    if (contractorTerritory?.id) {
                        dispatch(contractorsActions.deleteContractorTerritory({ contractorId: contractor.id as string, territoryId: contractorTerritory.id }, (succ) => {
                            if (succ) {
                                dispatch(contractorsActions.deactivateContractor(contractor.id as string, (succ) => {
                                    if (succ) {
                                        const hasSomeChanges = updateRequest.length > 0
                                        if (hasSomeChanges) {
                                            const updateRequestFiltered = updateRequest.filter((item) => item.path !== '/active')
                                            dispatch(contractorsActions.patchContractor({
                                                contractor: {
                                                    ...editContractorForm,
                                                },
                                                updateRequest: updateRequestFiltered
                                            }, (succ) => {
                                                if (succ) {
                                                    dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
                                                    toast.success.call('success', 'Contractor Deactivated')
                                                    setLoading(false)
                                                } else {
                                                    toast.error.call('error', 'Failed to deactivate contractor')
                                                    setLoading(false)
                                                }
                                            }))
                                        }
                                    } else {
                                        setLoading(false)
                                    }
                                }))
                            }
                        }))
                    } else {
                        dispatch(contractorsActions.deactivateContractor(contractor.id as string, (succ) => {
                            if (succ) {
                                const hasSomeChanges = updateRequest.length > 0
                                if (hasSomeChanges) {
                                    const updateRequestFiltered = updateRequest.filter((item) => item.path !== '/active')
                                    dispatch(contractorsActions.patchContractor({
                                        contractor: {
                                            ...editContractorForm,
                                        },
                                        updateRequest: updateRequestFiltered
                                    }, (succ) => {
                                        if (succ) {
                                            dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
                                            toast.success.call('success', 'Contractor Deactivated')
                                            setLoading(false)
                                        } else {
                                            toast.error.call('error', 'Failed to deactivate contractor')
                                            setLoading(false)
                                        }
                                    }))
                                }
                            } else {
                                setLoading(false)
                            }
                        }))
                    }
                }
            } else {
                const hasSomeChanges = updateRequest.length > 0
                if (hasSomeChanges) {
                    const updateRequestFiltered = updateRequest.filter((item) => item.path !== '/active')
                    dispatch(contractorsActions.patchContractor({
                        contractor: {
                            ...editContractorForm,
                        },
                        updateRequest: updateRequestFiltered
                    }, (succ) => {
                        if (succ) {
                            dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
                            toast.success.call('success', 'Contractor Updated')
                            setLoading(false)
                        } else {
                            toast.error.call('error', 'Failed to update contractor')
                            setLoading(false)
                        }
                    }))
                } else {
                    dispatchContext({ type: dispatchTypes.SET_VALUE, payload: { attr: 'modalOpen', value: false } })
                    setLoading(false)
                }
            }
        } else {
            setShowWarnings(true)
        }

    }

    const handleDelete = () => {
        dispatchContext({
            type: dispatchTypes.SET_VALUE, payload: {
                attr: 'modalType',
                value: MODAL_TYPE.DELETE_COMPANY
            }
        })
    }

    const handleTerritoryChange = (newSelected: SelectInputOption) => {
        setEditContractorTerritory(newSelected?.key ? { key: newSelected.key, label: newSelected.label } : null)
    }
    // FUTURE: multiple territories
    // const handleTerritoryChange = (newSelected: SelectInputOption | SelectInputOption[]) => {
    //     const options = Array.isArray(newSelected) ? newSelected : [newSelected]
    //     setEditContractorTerritories(options.map(opt => ({ key: opt.key, label: opt.label })))
    // }


    return (
        <PrivateLoader loading={loading}>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextFieldLabel
                        labelVariant='body1Bold'
                        label='Contractor Name:'
                        placeholder='Contractor Name'
                        value={editContractorForm?.name}
                        helperText={showWarnings && isEmpty(editContractorForm?.name) ? 'Should add Contractor Name' : ''}
                        error={showWarnings && isEmpty(editContractorForm?.name)}
                        onChange={(event) => setEditContractorForm({
                            ...editContractorForm,
                            name: event.target.value
                        })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextFieldLabel
                        labelVariant='body1Bold'
                        label='Contractor First Name:'
                        placeholder='Contractor First Name'
                        value={editContractorForm?.firstName}
                        helperText={showWarnings && isEmpty(editContractorForm?.firstName) ? 'Should add Contractor First Name' : ''}
                        error={showWarnings && isEmpty(editContractorForm?.firstName)}
                        onChange={(event) => setEditContractorForm({
                            ...editContractorForm,
                            firstName: event.target.value
                        })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextFieldLabel
                        labelVariant='body1Bold'
                        label='Contractor Last Name:'
                        placeholder='Contractor Last Name'
                        value={editContractorForm?.lastName}
                        helperText={showWarnings && isEmpty(editContractorForm?.lastName) ? 'Should add Contractor Last Name' : ''}
                        error={showWarnings && isEmpty(editContractorForm?.lastName)}
                        onChange={(event) => setEditContractorForm({
                            ...editContractorForm,
                            lastName: event.target.value
                        })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextFieldLabel
                        labelVariant='body1Bold'
                        type='email'
                        label='Contractor Email:'
                        placeholder='name@mail.com'
                        value={editContractorForm?.email}
                        helperText={showWarnings && (!isEmpty(editContractorForm?.email) && !validateEmail(editContractorForm?.email ? editContractorForm?.email : '')) ?
                            'Should add a valid Email' : ''}
                        error={showWarnings && (!isEmpty(editContractorForm?.email) && !validateEmail(editContractorForm?.email ? editContractorForm?.email : ''))}
                        onChange={(event) => setEditContractorForm({
                            ...editContractorForm,
                            email: event.target.value
                        })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextFieldLabel
                        labelVariant='body1Bold'
                        label='Website URL:'
                        placeholder='https://www.contractor-website.com'
                        value={'' + editContractorForm?.websiteUrl}
                        helperText={showWarnings && isEmpty(editContractorForm?.websiteUrl) ? 'Should add Website URL' : !isValidUrl(editContractorForm?.websiteUrl) ? 'Should add a valid Website URL' : ''}
                        error={showWarnings && isEmpty(editContractorForm?.websiteUrl) || !isValidUrl(editContractorForm?.websiteUrl)}
                        onChange={(event) => setEditContractorForm({
                            ...editContractorForm,
                            websiteUrl: event.target.value
                        })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextFieldLabel
                        labelVariant='body1Bold'
                        label='DocuSign Link:'
                        placeholder='https://www.contractor-docsign.com'
                        value={editContractorForm?.docuSignLink}
                        helperText={showWarnings && !isValidUrl(editContractorForm?.docuSignLink) ? 'Should add a valid DocuSign Link' : ''}
                        error={Boolean(showWarnings && !isValidUrl(editContractorForm?.docuSignLink))}
                        onChange={(event) => setEditContractorForm({
                            ...editContractorForm,
                            docuSignLink: event.target.value
                        })}
                    />
                </Grid>
                <Grid item xs={editContractorForm.active ? 6 : 12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editContractorForm?.active}
                                onChange={(event) => setEditContractorForm({
                                    ...editContractorForm,
                                    active: event.target.checked
                                })}
                                name='active'
                                color='infoText'
                            />
                        }
                        label={<Typography>Active</Typography>}
                    />
                </Grid>
                {editContractorForm.active && <Grid item xs={6}>
                    <SelectInput
                        label='Territory:'
                        onChange={handleTerritoryChange}
                        options={territories}
                        value={editContractorTerritory}
                        sx={{ minWidth: '156px', flex: '1' }}
                    />
                    {/* FUTURE: multiple territories - use multiple, editContractorTerritories, isOptionEqualToValue */}
                </Grid>}
                {/* <Grid item xs={12} sx={{
                    backgroundColor: '#FFF4F4!important',
                    borderRadius: '8px',
                    marginLeft: '13px'
                }}>
                    <Typography variant='body1Bold' sx={{ color: 'red!important' }}>Delete Contractor</Typography>
                    <Typography>You will no longer have access to all its information.</Typography>
                    <Button
                        onClick={handleDelete}
                        endIcon={<icons.Delete />}
                        variant='containedError'
                        sx={{
                            marginTop: '8px',
                            marginBottom: '8px'
                        }}
                    >
                        Delete Contractor
                    </Button>
                </Grid> */}
                <Grid item xs={6}>

                    <Button
                        variant='containedLight'
                        fullWidth
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>

                </Grid>
                <Grid item xs={6}>
                    <Button
                        onClick={handleSave}
                        variant='contained'
                        fullWidth
                        disabled={!hasUpdatedData}
                        endIcon={<icons.Save />}>
                        Save Changes
                    </Button>
                </Grid>
            </Grid>
        </PrivateLoader >
    )
}

export default EditContractor